/**
 * @file App.js
 * @description Componente raíz de la aplicación - Maneja routing y autenticación global
 * @module App
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { isAuthenticated, getUserInfo } from './services/authService';
import Login from './components/auth/Login';
import Dashboard from './Dashboard';
import './App.css';
import { LoadingScreen } from './Dashboard';
import TraceabilityPageConfig from './components/layout/traceability/TraceabilityPageConfig';
import TraceabilityPage from './components/layout/traceability/TraceabilityPage';
import SalesDashboard from './components/sales/SalesDashboard';

/**
 * Componente principal de la aplicación
 * 
 * Responsabilidades:
 * - Gestión de rutas principales (login, dashboard)
 * - Verificación de sesión al cargar
 * - Protección de rutas privadas
 * - Manejo del estado de autenticación global
 * 
 * @component
 * @returns {JSX.Element} Aplicación con routing configurado
 */
function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);

  /**
   * Callback ejecutado después de login exitoso
   * Actualiza el estado global del usuario autenticado
   * 
   * @param {Object} user - Información del usuario autenticado
   */
  const handleLoginSuccess = (user) => {
    setUserInfo(user);
  };

  /**
   * Maneja el cierre de sesión
   * Limpia tokens y estado del usuario
   */
  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userInfo');
    setUserInfo(null);
  };

  /**
   * Efecto: Verifica la sesión existente al montar el componente
   * 
   * Flujo:
   * 1. Verifica tokens en localStorage
   * 2. Valida tokens con authService
   * 3. Restaura sesión o limpia datos inválidos
   * 4. Escucha eventos de error de autenticación
   */
  useEffect(() => {
    /**
     * Verifica si existe una sesión válida almacenada
     */
    const checkSession = () => {
      try {
        // Verificar tokens almacenados
        const token = localStorage.getItem('accessToken');
        const userInfoStored = localStorage.getItem('userInfo');

        if (!token || !userInfoStored) {
          setUserInfo(null);
          setIsLoading(false);
          return;
        }

        // Validar tokens con el servicio de autenticación
        const auth = isAuthenticated();
        const user = getUserInfo();

        if (auth && user) {
          setUserInfo(user);
        } else {
          // Tokens inválidos - limpiar localStorage
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userInfo');
          setUserInfo(null);
        }
      } catch (err) {
        // Error al verificar - limpiar todo por seguridad
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userInfo');
        setUserInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    /**
     * Listener para errores de autenticación desde interceptores HTTP
     * Cierra sesión automáticamente si hay error 401
     * 
     * @param {CustomEvent} event - Evento con detalles del error
     */
    const handleAuthError = (event) => {
      setUserInfo(null);
    };

    // Registrar listener de errores
    window.addEventListener('authError', handleAuthError);

    // Ejecutar verificación de sesión (con delay para React Router)
    setTimeout(checkSession, 0);

    // Cleanup: remover listener al desmontar
    return () => {
      window.removeEventListener('authError', handleAuthError);
    };
  }, []);

  // Mostrar loading mientras verifica sesión
  if (isLoading) {
    return <LoadingScreen userInfo={userInfo} />;
  }

  return (
    <AuthProvider>
      <Router>
        <div className="App" style={{ textAlign: 'left' }}>
          <Routes>
          {/* Ruta pública: Login */}
          <Route
            path="/login"
            element={
              userInfo ?
                (userInfo.tipo_usuario === 'vendedor' ?
                  <Navigate to="/sales-dashboard" replace /> :
                  <Navigate to="/dashboard" replace />) :
                <Login onLoginSuccess={handleLoginSuccess} />
            }
          />

            {/* Ruta protegida: Dashboard */}
            <Route
              path="/dashboard"
              element={
                userInfo ?
                  (userInfo.tipo_usuario === 'vendedor' ? 
                    <Navigate to="/sales-dashboard" replace /> : 
                    <Dashboard userInfo={userInfo} onLogout={handleLogout} />) :
                  <Navigate to="/login" replace />
              }
            />

            {/* Ruta protegida: Sales Dashboard */}
            <Route
              path="/sales-dashboard"
              element={
                userInfo ?
                  (userInfo.tipo_usuario === 'vendedor' ? 
                    <SalesDashboard userInfo={userInfo} onLogout={handleLogout} /> : 
                    <Navigate to="/dashboard" replace />) :
                  <Navigate to="/login" replace />
              }
            />

            {/* Ruta raíz: Redirige a dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Catch-all: Rutas no encontradas van a login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
            {/* Ruta protegida: Seguimiento en Tiempo Real */}
            <Route
              path="/traceabilityConfig"
              element={
                userInfo
                  ? <TraceabilityPageConfig />
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/traceability"
              element={
                userInfo
                  ? <TraceabilityPage />
                  : <Navigate to="/login" replace />
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;